import {
  Component,
  OnChanges,
  Input,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import * as _ from 'lodash';

import { TableComponent } from 'shared/table/table/table.component';
import { AppModalService } from 'shared/modal/modal.service';
import { AddEditUserModalComponent } from 'shared/team/add-edit-user-modal/add-edit-user-modal.component';
import { UserService } from 'shared/user/user.service';
import { ADD_EDIT_USER_MODAL_NAME } from 'shared/modal/modal-names';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SubscriptionService } from 'shared/subscription/subscription.service';

import { SubscriptionWidgetComponent } from 'shared/subscription/subscription-widget/subscription-widget.component';
import {ProductPanelComponent} from '../../../../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'team-form',
  templateUrl: 'team-form.component.html',
  styleUrls: ['team-form.component.scss'],
})

export class TeamFormComponent extends TableComponent implements OnChanges {
  teamHeaders: any[] = [{
    name: 'initials',
    size: 1,
  }, {
    name: 'name',
    size: 9,
  }, {
    name: 'email',
    size: 9,
  }, {
    name: 'phone',
    size: 6,
  }, {
    name: 'last_accessed',
    size: 6,
  }];
  supportHeaders: any[] = [{
    name: 'initials',
    size: 1,
  }, {
    name: 'name',
    size: 9,
  }, {
    name: 'text',
    size: 15,
  }, {
    name: 'last_accessed',
    size: 6,
  }];
  form: UntypedFormGroup;
  user: any;
  editingUserData: any;

  @Input() brandId: string;

  @Output() addEditDone = new EventEmitter();

  @Output() forceSave = new EventEmitter();

  @ViewChild('addEditUserModal', {static: false})
  addEditUserModal: AddEditUserModalComponent | void;

  @ViewChild(SubscriptionWidgetComponent, {static: false})
  subscrWidget: SubscriptionWidgetComponent | void;

  constructor(
    private fb: UntypedFormBuilder,
    private appModalService: AppModalService,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
    private permissionsService: PermissionsService,
  ) {
    super();
    this.createForm();
  }

  // getters
  get formData(): UntypedFormArray {
    return this.form.get('formData') as UntypedFormArray;
  }

  get isInValid(): boolean {
    return this.form.invalid;
  }

  get isAdmin(): boolean {
    return this.permissionsService.hasPermission(['admin']);
  }

  // hooks
  ngOnChanges(changes: any) {
    this.userService.getUser()
    .subscribe(user => {
      this.user = user;
    });
    this.setValidators();
    this.setFormData();
  }

  // public
  createForm(): void {
    this.form = this.fb.group({
      formData: this.fb.array([]),
    });
  }

  setFormData(): void {
    this.data = _.orderBy(this.data, ['type'], ['desc']);
    const FGs = this.data.map((item: any) => {
      return this.fb.group(item);
    });
    this.form.setControl('formData', this.fb.array(FGs));
  }

  // handlers
  onInviteNewUserClick() {
    this.editingUserData = null;
    _.defer(() => this.addEditUserModal && this.addEditUserModal.show());
  }

  onEditUserClick(fromGroup: UntypedFormGroup) {
    this.editingUserData = fromGroup.value;
    _.defer(() => this.addEditUserModal && this.addEditUserModal.show());
  }

  onAddEditDone() {
    this.addEditDone.emit();
  }

  onTableEditingChanged(editing: boolean) {
    // editing was finished
    if (!editing) {
      this.forceSave.emit();
    }
  }

  onInviteUserError() {
    if (this.addEditUserModal) {
      this.addEditUserModal.hide();
    }
    if (this.subscrWidget) {
      this.subscrWidget.openPlanWarningUsersModal();
    }
  }

  hasPermission(allow: Array<string>|string, requireAll: boolean= false): boolean {
    return this.permissionsService.hasPermission(allow, requireAll);
  }

  allowHover(formGroup: any): boolean {
    // Only admins can edit
    if (!this.hasPermission('admin')) {
      return false;
    }

    // A user can not edit their record
    if (formGroup.get('user_id').value === this.user.id) {
      return false;
    }

    // Support user permissions can not be edited
    if (formGroup.get('type').value === 'support') {
      return false;
    }

    return true;
  }

  // private
  private getControlValue(formGroup: UntypedFormGroup, header: string) {
    const control = formGroup.controls ? formGroup.controls[header] : null;
    return control ? control.value : '';
  }

  private getUserFirstName(user: any) {
    const name = user.name;
    const nameArray = name.split(' ');
    if (nameArray.length) {
      return nameArray[0];
    }
    return '';
  }

  private getUserLastName(user: any) {
    const name = user.name;
    const nameArray = name.split(' ');
    if (nameArray.length > 1) {
      return nameArray[1];
    }
    return '';
  }

  private canDelete(fromGroup: UntypedFormGroup) {
    if (!this.user) { return false; }
    const teamUser = fromGroup.value;
    /*
      allow teamUser deletion if:
      - current user is rxspeed user,
      - teamUser id differs from current user id,
      - teamUser is not rxspeed user.
    */
    return teamUser.user_id !== this.user.id &&
      (this.user.is_rxspeed || teamUser.type === 'user');
  }
}
